import { CompensationType } from '../../shared/constants';

/**
 * Transforms posting details entity from API into form data structure
 *
 * @param {import('../../../../../domain/PermanentJobs/types/PostingDetails').IPostingDetailsEntity} payload - Posting details entity from API
 * @returns {Object} Form data object compatible with usePostPermanentJobForm
 * @returns {string} returns.professionType - Profession type (subProfession takes precedence if available)
 * @returns {string} returns.minRate - Minimum rate as string
 * @returns {string} returns.maxRate - Maximum rate as string
 * @returns {string[]} returns.benefits - Array of benefit types
 * @returns {string} returns.benefitsOther - Other benefits description
 * @returns {string} returns.compensationType - Inferred compensation type (FIXED or RANGE)
 * @returns {number[]} returns.expectedDays - Expected work days array
 * @returns {Object} returns.jobDescription - Job description object
 * @returns {string} returns.jobDescription.raw - HTML job description
 * @returns {string} returns.jobDescription.text - Plain text job description
 * @returns {string} returns.jobTitle - Job title
 * @returns {string} returns.officeSpecialty - Office specialty
 * @returns {string} returns.payType - Pay type (Hourly or Salary)
 * @returns {string} returns.planId - Payment plan ID
 * @returns {string} returns.recruiterEmail - Recruiter email address
 * @returns {string} returns.scheduleType - Schedule type (Full Time, Part Time, Flexible)
 * @returns {string} returns.subProfession - Sub-profession type
 * @returns {Object|null} returns.office - Office object (set to null, should be populated separately)
 *
 */
export const transformFromPostingDetailsEntityToFormData = (payload) => {
  // Business logic: subProfession takes precedence over professionType
  const professionType = payload?.subProfession || payload?.professionType || '';

  // Convert numeric rates to strings for form compatibility
  const minRate = payload?.salaryRange?.min !== undefined ? String(payload.salaryRange.min) : '';
  const maxRate = payload?.salaryRange?.max !== undefined ? String(payload.salaryRange.max) : '';

  return {
    professionType,
    minRate,
    maxRate,
    benefits: payload?.benefits || [],
    benefitsOther: payload?.benefitsOther || '',
    compensationType: inferCompensationType(minRate, maxRate),
    expectedDays: payload?.expectedDays || [],
    jobDescription: {
      raw: payload?.description || '',
      text: '',
    },
    jobTitle: payload?.title || '',
    officeSpecialty: payload?.specialty || '',
    payType: payload?.salaryRange?.payType || 'Hourly',
    planId: payload?.planId || '',
    recruiterEmail: payload?.recruiterEmail || '',
    scheduleType: payload?.scheduleType || '',
    subProfession: payload?.subProfession || '',
    office: null,
  };
};

/**
 * Infers compensation type from min and max rates
 *
 * Business logic:
 * - FIXED: when minRate == maxRate or only minRate exists
 * - RANGE: when minRate != maxRate
 * - Default: FIXED when no rates provided
 *
 * @param {string} minRate - Minimum rate as string
 * @param {string} maxRate - Maximum rate as string
 * @returns {string} CompensationType.FIXED or CompensationType.RANGE
 *
 * @example
 * inferCompensationType('35', '35') // Returns CompensationType.FIXED
 * inferCompensationType('35', '50') // Returns CompensationType.RANGE
 * inferCompensationType('35', '') // Returns CompensationType.FIXED
 * inferCompensationType('', '') // Returns CompensationType.FIXED
 */
const inferCompensationType = (minRate, maxRate) => {
  // No rates provided - default to FIXED
  if (!minRate && !maxRate) {
    return CompensationType.FIXED;
  }

  // Only minRate provided - FIXED
  if (minRate && !maxRate) {
    return CompensationType.FIXED;
  }

  // Both rates provided - compare values
  if (minRate && maxRate) {
    const min = parseFloat(minRate.replace(/,/g, ''));
    const max = parseFloat(maxRate.replace(/,/g, ''));

    // Same values - FIXED
    if (min === max) {
      return CompensationType.FIXED;
    }

    // Different values - RANGE
    return CompensationType.RANGE;
  }

  // Default fallback
  return CompensationType.FIXED;
};
